<script setup lang="ts">
/**
 * "or continue with" + Google (Figma `66:46882`).
 *
 * Google only. VK and Yandex were dropped from the UI in the redesign; the
 * store still speaks to all three providers (`useAuthStore.thirdPartyAuth`), so
 * bringing a button back is a markup change, nothing more.
 *
 * Closing the OAuth popup is a user-initiated cancel, not a failure: no toast,
 * no error state, the button simply returns to rest (`66:47535`).
 */
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore, useAuthModal, useTokenStore } from '@/features/auth/store'
import { useFairToast } from '@/ui/patterns/FairToast'
// The flow's own Google mark (`I66:46901;261:8511`) — its palette differs from
// the Material-style logo in `assets/img/Google.svg` used elsewhere.
import googleLogoUrl from '@/features/auth/ui/icons/google-coloured.svg'

const emit = defineEmits<{ signedIn: [] }>()

const { t } = useI18n()
const toast = useFairToast()
const pending = ref(false)

/** Thrown when the user closes the popup — expected, reported to nobody. */
const CANCEL_MESSAGES = ['Popup closed by user', 'Third-party auth cancelled']

async function handleGoogle() {
  if (pending.value) return
  pending.value = true
  try {
    await useAuthStore().thirdPartyAuth('google')

    // The popup wrote the tokens to localStorage; lift them into the store.
    const stored = window.localStorage.getItem('userTokens')
    if (stored) {
      const tokenStore = useTokenStore()
      const parsed = JSON.parse(stored)
      tokenStore.accessToken = parsed?.accessToken
      tokenStore.refreshToken = parsed?.refreshToken
      tokenStore.expiresIn = parsed?.expiresIn
      tokenStore.refreshExpiresIn = parsed?.refreshExpiresIn
      tokenStore.scheduleTokenRefresh()
    }

    await useAuthStore().getUserInfo()
    toast.show({ message: t('auth.toast.signed_in'), tone: 'success' })
    useAuthModal().hideAuthModal()
    emit('signedIn')
  } catch (error) {
    if (error instanceof Error && CANCEL_MESSAGES.includes(error.message)) return
    toast.show({ message: t('auth.errors.generic'), tone: 'warning' })
  } finally {
    pending.value = false
  }
}
</script>

<template>
  <div class="fair-auth-social">
    <span class="fair-auth-social__divider">{{ t('auth.social.divider') }}</span>
    <button
      type="button"
      class="fair-auth-button fair-auth-social__google"
      :disabled="pending"
      @click="handleGoogle"
    >
      <img
        class="fair-auth-social__google-logo"
        :src="googleLogoUrl"
        alt=""
      >
      {{ t('auth.social.google') }}
    </button>
  </div>
</template>
